import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { browserTz, conferenceLabel, durationsOf, fmtMinutes, publicApi, type Booking, type PublicLink } from '../api';
import { ErrorBox, Loading, PublicFrame } from '../components/bk';
import { SlotPicker } from '../components/SlotPicker';

/** The page end customers see at /b/:slug. */
export function PublicBook() {
  const { slug } = useParams();
  const [link, setLink] = useState<PublicLink | null>(null);
  const [tz, setTz] = useState(browserTz());
  const [pick, setPick] = useState('');
  const [form, setForm] = useState({ name: '', email: '', notes: '', guests: '' });
  const [done, setDone] = useState<Booking | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [chosen, setChosen] = useState<number | null>(null);

  useEffect(() => {
    publicApi.get<PublicLink>(`/api/public/links/${slug}`).then(setLink).catch((e) => setError(e.message));
  }, [slug]);

  if (!link) return <PublicFrame>{error ? <ErrorBox error={error} /> : <Loading />}</PublicFrame>;

  const options = durationsOf(link);
  const duration = chosen ?? options[0];

  const book = async () => {
    setBusy(true);
    setError('');
    try {
      const r = await publicApi.post<{ booking: Booking }>(`/api/public/links/${slug}/book`, {
        start: pick,
        name: form.name,
        email: form.email,
        notes: form.notes,
        guests: form.guests.split(/[,;\s]+/).filter(Boolean),
        timezone: tz,
        durationMinutes: duration,
      });
      setDone(r.booking);
    } catch (e) {
      setError((e as Error).message);
      if ((e as { code?: string }).code === 'slot_taken') {
        setPick('');
        setReloadKey((k) => k + 1);
      }
    } finally {
      setBusy(false);
    }
  };

  const hostNames = link.hosts.map((h) => h.name).join(', ');

  if (done) {
    const s = DateTime.fromISO(done.start).setZone(tz);
    return (
      <PublicFrame>
        <section className="card center">
          <div style={{ fontSize: 40 }}>✅</div>
          <h1>You're booked</h1>
          <p><strong>{link.title}</strong> with {hostNames}</p>
          <p className="when">{s.toFormat('cccc, LLLL d')} · {s.toFormat('h:mm a')} – {DateTime.fromISO(done.end).setZone(tz).toFormat('h:mm a ZZZZ')}</p>
          <p className="muted">A calendar invitation and a confirmation email are on their way to {done.inviteeEmail}. The email has links to reschedule or cancel.</p>
        </section>
      </PublicFrame>
    );
  }

  return (
    <PublicFrame>
      <section className="card">
        <div className="hostline">{hostNames}</div>
        <h1>{link.title}</h1>
        <div className="meta">
          <span>⏱ {options.map(fmtMinutes).join(' / ')}</span>
          <span>📞 {conferenceLabel[link.conferenceType]}</span>
        </div>
        {link.description && <p style={{ whiteSpace: 'pre-line' }}>{link.description}</p>}
      </section>

      <ErrorBox error={error} />

      <section className="card">
        <h2>{pick ? 'Your details' : 'Pick a time'}</h2>
        {!pick && options.length > 1 && (
          <div className="field">
            <label id="len-label">How long do you need?</label>
            <div className="chips" role="radiogroup" aria-labelledby="len-label">
              {options.map((m) => (
                <button
                  key={m}
                  type="button"
                  role="radio"
                  aria-checked={m === duration}
                  className={`chip ${m === duration ? 'on' : ''}`}
                  onClick={() => setChosen(m)}
                >
                  {fmtMinutes(m)}
                </button>
              ))}
            </div>
          </div>
        )}
        {!pick ? (
          <SlotPicker
            key={`${reloadKey}-${duration}`}
            timezone={tz}
            onTimezone={setTz}
            maxDaysAhead={link.maxDaysAhead}
            selected={pick}
            onPick={setPick}
            fetchSlots={(from, to) =>
              publicApi.get<{ slots: string[] }>(`/api/public/links/${slug}/slots?from=${from}&to=${to}&duration=${duration}`).then((r) => r.slots)
            }
          />
        ) : (
          <form onSubmit={(e) => (e.preventDefault(), book())}>
            <p className="when">
              {DateTime.fromISO(pick).setZone(tz).toFormat('cccc, LLLL d · h:mm a')} · {fmtMinutes(duration)} <span className="muted small">({tz})</span>{' '}
              <button type="button" className="link-btn" onClick={() => setPick('')}>Change</button>
            </p>
            <div className="field">
              <label htmlFor="n">Name</label>
              <input id="n" type="text" required autoComplete="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="e">Email</label>
              <input id="e" type="email" required autoComplete="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="g">Other guests (optional)</label>
              <input id="g" type="text" placeholder="email1@company.com, email2@company.com" value={form.guests} onChange={(e) => setForm({ ...form, guests: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="no">Anything to share before the meeting?</label>
              <textarea id="no" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <button className="primary" type="submit" disabled={busy} style={{ width: '100%', justifyContent: 'center' }}>
              {busy ? 'Booking…' : 'Confirm booking'}
            </button>
          </form>
        )}
      </section>
    </PublicFrame>
  );
}
